import { useEffect, useState } from 'react';
import { Loader2, FileSignature, Send, Copy, PencilLine, RefreshCw, CheckCircle2, ChevronRight } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';
import MilestonesManager from './MilestonesManager';

const STATUS = {
  to_prepare: { l: 'À préparer', c: 'bg-gray-200 text-gray-700' },
  sent: { l: 'Envoyé', c: 'bg-blue-100 text-blue-700 border border-blue-300' },
  waiting_acceptance: { l: 'En attente de signature', c: 'bg-amber-100 text-amber-800 border border-amber-400' },
  accepted: { l: 'Signé', c: 'bg-green-100 text-green-700 border border-green-400' },
  refused: { l: 'Refusé', c: 'bg-red-100 text-red-700 border border-red-400' },
};

/** Step 04 admin block: contract per RFQ + milestones panel */
export default function ContractManager({ rfqId, clientEmail, mandateAccepted }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [contract, setContract] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/rfq/${rfqId}/contract`);
      setContract(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId]);

  const prepare = async () => {
    setBusy(true);
    try { await api.post(`/admin/rfq/${rfqId}/contract/prepare`); toast({ title: 'Contrat préparé', description: 'Snapshot à partir du modèle courant.', duration: 8000 }); load(); }
    catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 }); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/rfq/${rfqId}/contract/send`);
      toast({ title: 'Contrat envoyé', description: res.data?.emailSent ? `Lien envoyé à ${clientEmail}` : 'Lien généré (courriel non envoyé).', duration: 10000 });
      load();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 }); }
    finally { setBusy(false); }
  };
  const copyLink = async () => {
    try {
      const res = await api.post(`/admin/rfq/${rfqId}/contract/send`);
      await navigator.clipboard.writeText(res.data.url);
      toast({ title: 'Lien copié', description: 'Collez-le dans un courriel si nécessaire.', duration: 6000 });
      load();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 }); }
  };

  if (loading) return <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Chargement du contrat…</div>;

  const exists = contract?.exists;
  const status = contract?.status || 'to_prepare';
  const info = STATUS[status] || STATUS.to_prepare;
  const accepted = status === 'accepted';

  return (
    <div className="bg-white border-2 border-brand-yellow/40 rounded-lg overflow-hidden" data-testid={`contract-manager-${rfqId}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 p-4 hover:bg-brand-navy/5 transition">
        <div className="flex items-center gap-2 flex-wrap">
          <FileSignature className="w-4 h-4 text-brand-navy" />
          <h4 className="font-bold text-brand-navy text-sm">Étape 04 · Contrat et suivi</h4>
          <span data-testid="contract-status-badge" className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${info.c}`}>{info.l}</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-brand-navy transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="p-4 border-t border-gray-100 space-y-4">
          {!mandateAccepted && (
            <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
              Le mandat client doit être accepté avant la signature du contrat.
            </div>
          )}
          {contract?.acceptedAt && (
            <div className="p-2 rounded bg-green-50 border border-green-300 text-xs text-brand-navy flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
              <span>Signé le {new Date(contract.acceptedAt).toLocaleString('fr-CA')} par <b>{contract.signerName}</b> ({contract.signerCompany}) · méthode : <b>{contract.method === 'docusign' ? 'DocuSign' : 'interne'}</b> · IP {contract.signerIP || '—'}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {!exists && !accepted && (
              <button data-testid="prepare-contract-btn" disabled={busy} onClick={prepare} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-navy text-brand-navy text-xs font-bold hover:bg-brand-navy hover:text-white disabled:opacity-50">
                <PencilLine className="w-3.5 h-3.5" /> Préparer le contrat
              </button>
            )}
            {exists && !accepted && (
              <>
                <button data-testid="send-contract-btn" disabled={busy || !clientEmail} onClick={send} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-50">
                  <Send className="w-3.5 h-3.5" /> Envoyer au client
                </button>
                <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
                  <Copy className="w-3.5 h-3.5" /> Copier le lien
                </button>
                <button onClick={prepare} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
                  <RefreshCw className="w-3.5 h-3.5" /> Régénérer
                </button>
              </>
            )}
          </div>
          {accepted && <MilestonesManager rfqId={rfqId} />}
        </div>
      )}
    </div>
  );
}
